import React, { useCallback, useState, useEffect } from 'react';
import { Button, Row, Form, Col } from 'reactstrap';
import { FaFileArrowUp } from 'react-icons/fa6';
import ReactButton from 'components/Buttons/ReactButton';
import DefaultModal from 'components/Modal/DefaultModal';
import { DragAndDrop } from 'components/DragAndDrop';
import { FlexMediaPreviewer } from 'components/FlexMediaPreviewer';
import TemplateService from '../service';
import { toast } from 'react-toastify';

const ALLOWED_EXTENSIONS_BY_MIME_TYPE = {
  VIDEO: '.mp4, .webm, .ogv',
  DOCUMENT: '.pdf, .txt, .rtf',
  IMAGE: '.png, .jpg, .jpeg, .gif, .webp, .bmp, .heif, .heic'
};

const ALL_ALLOWED_EXTENSIONS = Object.values(
  ALLOWED_EXTENSIONS_BY_MIME_TYPE
)?.join?.(',');

export const UpdateMediaButton = ({ template, onMediaUpdate }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedFile(null);
    setPreviewFile(null);
  }, []);

  const fetchFileFromUrl = async (url, filename = 'media') => {
    const response = await fetch(url);
    const blob = await response.blob();
    const type = blob.type || 'application/octet-stream';
    return new File([blob], filename, { type });
  };

  useEffect(() => {
    if (isModalOpen && template?.id) {
      (async () => {
        try {
          const response = await TemplateService.getTemplateById(template.id);
          const mediaUrl = response?.data?.media ?? template?.media;
          if (mediaUrl) {
            const file = await fetchFileFromUrl(mediaUrl, 'media');
            setPreviewFile(file);
          }
        } catch (error) {
          console.error('Erro ao buscar template:', error);
        }
      })();
    }
  }, [isModalOpen, template?.id]);

  useEffect(() => {
    if (selectedFile) {
      setPreviewFile(selectedFile);
    }
  }, [selectedFile]);

  const handleFileChange = (e) => {
    const MAX_VIDEO_SIZE_MB = 16;
    const MAX_DOCUM_SIZE_MB = 100;

    const file = e?.target?.files?.[0] ?? e?.[0];
    if (!file) {
      return toast.error('Não foi possível selecionar o(s) arquivo(s)', {
        autoClose: 3000
      });
    }

    const { type, size } = file;
    const sizeInMb = size / 1000000;

    if (type.includes('video') && sizeInMb > MAX_VIDEO_SIZE_MB)
      return toast.error(`Vídeos devem ter no máximo ${MAX_VIDEO_SIZE_MB}MB`, {
        autoClose: 3000
      });

    if (
      (type.includes('application') || type.includes('text')) &&
      sizeInMb > MAX_DOCUM_SIZE_MB
    )
      return toast.error(
        `Documentos devem ter no máximo ${MAX_DOCUM_SIZE_MB}MB`,
        { autoClose: 3000 }
      );

    setSelectedFile(file);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      const formData = new FormData();
      formData.append('id', template.id);
      if (selectedFile) formData.append('file', selectedFile);

      const response = await TemplateService.updateTemplateMedia(formData);
      if (onMediaUpdate && response?.data?.media) {
        onMediaUpdate(template.id, response.data.media);
      }

      handleCloseModal();
    } catch (error) {
      console.error('Erro ao salvar mídia:', error);
    }
  };

  return (
    <>
      <Button
        color="info"
        title="Editar Media"
        type="button"
        size="sm"
        onClick={handleOpenModal}
      >
        <FaFileArrowUp />
      </Button>

      <DefaultModal
        show={isModalOpen}
        onModalToggle={(open) => {
          if (!open) handleCloseModal();
        }}
        title={`Enviar Media - ${template.name || 'Sem Nome'}`}
        showCloseButton={false}
        modalContainerClasses="modal-sm"
      >
        <Form onSubmit={handleSave}>
          <Row className="mb-3">
            <Col sm="12">
              <DragAndDrop
                acceptedFileExtensions={ALL_ALLOWED_EXTENSIONS}
                name="update_media_file"
                id="update_media_file"
                changeFile={handleFileChange}
                description="Selecione um arquivo"
              />
            </Col>
          </Row>
          {previewFile && (
            <Row className="mb-3">
              <Col sm="12">
                <FlexMediaPreviewer file={previewFile} />
              </Col>
            </Row>
          )}
          <hr />
          <ReactButton btnColor="confirmation" type="submit">
            Salvar
          </ReactButton>
          <ReactButton
            btnColor="cancelation"
            type="button"
            onClick={handleCloseModal}
          >
            Fechar
          </ReactButton>
        </Form>
      </DefaultModal>
    </>
  );
};

export default UpdateMediaButton;
